import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Image as ImageIcon, Mail, MessageSquare, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const EcardRow = ({ r, patientId, onResent }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const sms = r.channel === 'sms';
  const resend = async () => {
    if (!window.confirm(`Resend "${r.title}" by ${sms ? 'text' : 'email'} to ${r.to}?`)) return;
    setBusy(true);
    const common = { patient_id: patientId, headline: r.headline, note: r.note };
    try {
      await axios.post(`${BACKEND}/api/ecards/${r.cardId}/${sms ? 'send-sms' : 'send-email'}`,
        sms ? { ...common, to_phone: r.to } : { ...common, to_email: r.to }, auth());
      toast({ title: 'Resent', description: `Sent again to ${r.to}` });
      onResent();
    } catch (e) {
      toast({ title: 'Resend failed', description: e.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex gap-4 p-3 border rounded-lg bg-white" data-testid={`patient-ecard-${r.id}`}>
      <img src={`${BACKEND}/api/ecards/${r.cardId}/thumbnail`} alt={r.title} loading="lazy" className="h-28 w-20 object-contain bg-slate-50 rounded border shrink-0" />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="font-semibold text-gray-900">{r.title}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
          {sms ? <MessageSquare className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
          {sms ? 'Text' : 'Email'} to {r.to} · {fmt(r.sentAt)}{r.sentByName ? ` · by ${r.sentByName}` : ''}
        </p>
        {r.headline && <p className="text-sm text-gray-700" data-testid={`patient-ecard-headline-${r.id}`}><span className="text-gray-400">Headline:</span> {r.headline}</p>}
        {r.note && <p className="text-sm text-gray-700" data-testid={`patient-ecard-note-${r.id}`}><span className="text-gray-400">Note:</span> {r.note}</p>}
      </div>
      <Button size="sm" variant="outline" onClick={resend} disabled={busy || !r.to} className="self-start" data-testid={`patient-ecard-resend-${r.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-1" />Resend</>}
      </Button>
    </li>
  );
};

export const PatientEcardsTab = ({ patientId }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    axios.get(`${BACKEND}/api/patient-records/${patientId}/ecards`, auth()).then((r) => setItems(r.data.data || [])).catch(() => setItems([]));
  }, [patientId]);
  useEffect(() => { setItems(null); load(); }, [load]);
  if (!items) return <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-pink-600" /></div>;
  if (!items.length) {
    return (
      <div className="text-center py-10 text-gray-500" data-testid="patient-ecards-empty">
        <ImageIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
        <p>No eCards sent to this patient yet</p>
        <Button size="sm" className="mt-2 bg-pink-600 hover:bg-pink-700" onClick={() => { window.location.href = '/patient-ecards'; }} data-testid="patient-ecards-open-library-btn">Send an eCard</Button>
      </div>
    );
  }
  return <ul className="space-y-3 pt-1 max-h-[560px] overflow-y-auto" data-testid="patient-ecards-tab">{items.map((r) => <EcardRow key={r.id} r={r} patientId={patientId} onResent={load} />)}</ul>;
};

export default PatientEcardsTab;
